import { MyAcknowledgementRequestDtoItemType } from "@/generated/model";
import type { EntityType } from "@/lib/icons/categories";

/**
 * The route to open for an acknowledgeable item, as a discriminated union.
 *
 * Deliberately optional-returning, exactly like `categoryDestination`: a map
 * indexed with a cast would type the result as always-defined, and the caller
 * would render a dead link for a type that has no home yet. Returning
 * `undefined` makes the caller's guard compiler-enforced.
 */
export type AcknowledgementItemDestination = {
  to: "/event-records/$eventRecordId";
  params: { eventRecordId: string };
};

export interface AcknowledgementItemMeta {
  /** Plural label for lists and filters. */
  label: string;
  /** Singular, for sentences like "this policy". */
  singular: string;
  /** Feeds TintedEntityIcon — these icon slugs already exist in the app. */
  entity: EntityType;
}

/**
 * Keyed off the GENERATED enum, so when the server starts returning POLICY the
 * build fails here until this map gains an entry — rather than silently
 * rendering a blank label. Same guard as `OUTCOME_BADGE` in the staff
 * login-attempts route.
 */
export const ITEM_TYPE_META: Record<
  MyAcknowledgementRequestDtoItemType,
  AcknowledgementItemMeta
> = {
  [MyAcknowledgementRequestDtoItemType.EVENT_RECORD]: {
    label: "Events",
    singular: "event record",
    entity: "event",
  },
  [MyAcknowledgementRequestDtoItemType.RISK_ASSESSMENT]: {
    label: "Risk assessments",
    singular: "risk assessment",
    entity: "risk-assessment",
  },
  [MyAcknowledgementRequestDtoItemType.POLICY]: {
    label: "Policies",
    singular: "policy",
    entity: "policy-or-procedure",
  },
};

/** Falls back to something readable rather than throwing on an unknown type. */
export function itemTypeMeta(
  itemType: MyAcknowledgementRequestDtoItemType | string,
): AcknowledgementItemMeta {
  return (
    ITEM_TYPE_META[itemType as MyAcknowledgementRequestDtoItemType] ?? {
      label: "Documents",
      singular: "document",
      entity: "event",
    }
  );
}

/**
 * Where to send someone to see the item itself.
 *
 * Undefined for RISK_ASSESSMENT and POLICY: the hubs that own them do not exist,
 * so there is nowhere to go. Callers render the title as plain text instead.
 */
export function itemDestination(
  itemType: MyAcknowledgementRequestDtoItemType | string,
  sourceId: string | null,
): AcknowledgementItemDestination | undefined {
  if (!sourceId) {
    return undefined;
  }
  if (itemType === MyAcknowledgementRequestDtoItemType.EVENT_RECORD) {
    return {
      to: "/event-records/$eventRecordId",
      params: { eventRecordId: sourceId },
    };
  }
  return undefined;
}
